'use client';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ENDPOINTS } from '@/config/endpoints';
import GlobalBuyerData from '@/config/global_buyerData';
import { ColorRing } from 'react-loader-spinner';
import { sendOTPForTermsheet, verifyOTPForTermsheet } from '@/lib/credlixBypass';
import Toast from './Toast';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const schema = yup.object({
  Name: yup.string().required('Full Name is required.'),
  CompanyName: yup.string().required('Company Name is required.'),
  Email: yup.string().required('Email is required.').email('Please enter a valid email address.'),
  Phone: yup
    .string()
    .required('Mobile number is required.')
    .test('phone-validation', 'Invalid mobile number.', function (value) {
      const country = this.parent.country;
      if (country === 'India') return /^[6-9]\d{9}$/.test(value);
      return /^\d{6,12}$/.test(value);
    }),
  country: yup.string().required('Country is required.'),
});

const QUOTESFORMPOPUP = ({ setOpenQuotesPopup, setQuoteFlag, fetchQuotesData }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [phVerifiedCfForm, setPhVerifiedCfForm] = useState(false);
  const [otpBtnDisabled, setOtpBtnDisabled] = useState(true);
  const [loader, setLoader] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [getOtp, setGetOtp] = useState(false);
  const [otpIncorrect, setOtpIncorrect] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [requestIdCfForm, setRequestIdCfForm] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [otpSentNotification, setOtpSentNotification] = useState(false);
  const [isResendMode, setIsResendMode] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [nameTyped, setNameTyped] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const phoneValue = watch('Phone');

  const handleCaptchaSubmitCfForm = useCallback(async () => {
    if (!executeRecaptcha) return false;
    try {
      return await executeRecaptcha('quotesFormSubmit');
    } catch (error) {
      console.error('Error executing reCaptcha:', error);
      return false;
    }
  }, [executeRecaptcha]);

  const checkPhoneCfForm = (e) => {
    let value = e.target.value;

    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');

    // Update the input value to only numeric characters
    e.target.value = value;

    const regex = /^[0-9]*$/;
    const selectedCountryName = watch('country');
    const isIndian = selectedCountryName === 'India';

    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage('');
    }

    if (value && value.length === 10 && regex.test(value) && !/^[0123]/.test(value)) {
      if (isIndian) {
        setOtpBtnDisabled(false);
      } else {
        // For non-Indian numbers, mark as verified automatically
        setPhVerifiedCfForm(true);
        setOtpBtnDisabled(true);
      }
      setValue('Phone', value);
    } else {
      setOtpBtnDisabled(true);
      if (!isIndian) {
        setPhVerifiedCfForm(false);
      }
    }
  };
  const handleNameInput = (e) => {
    if (!nameTyped) {
      pushCleverTapEvent("Shipping_Calculator_Quote_Start", {
        "PageName": "Shipping_Calculator"
      });
      setNameTyped(true);
    }
  };

  const sendOTPCfForm = async () => {
    const captchaToken = await handleCaptchaSubmitCfForm();
    if (!captchaToken) {
      setErrorMessage('reCAPTCHA verification failed.');
      return;
    }

    setLoader(true);
    setShowOtpField(true);
    setErrorMessage('');

    try {
      const response = await sendOTPForTermsheet(phoneValue, captchaToken);
      const res = await response.json();
      if (response.status === 200) {
        setGetOtp(true);
        setRequestIdCfForm(res.result.requestId);
        setIsResendMode(true);
        
        // Show success toast
        setToast({
          message: 'OTP sent successfully to your mobile number!',
          type: 'success',
        });
        
        window.gtag('event', 'sendOTPfinance', {
          event_category: 'Get financed',
          event_label: 'Send OTP',
        });
        startCountdownCfForm();
      } else {
        setErrorMessage('Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage('Error sending OTP. Please try again.');
    } finally {
      setLoader(false);
      startCountdownCfForm();
    }
  };

  const startCountdownCfForm = () => {
    setCountdown(30);
    setOtpBtnDisabled(true);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setOtpBtnDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const verifyOTPCfForm = async (enteredOtp) => {
    const captchaToken = await handleCaptchaSubmitCfForm();
    if (!captchaToken) {
      setErrorMessage('reCAPTCHA verification failed.');
      return;
    }

    setLoader(true);

    try {
      const response = await verifyOTPForTermsheet(requestIdCfForm, enteredOtp, captchaToken);
      if (response.status === 200) {
        setPhVerifiedCfForm(true);
        setOtpIncorrect(false);
        setShowOtpField(false);
        pushCleverTapEvent("Shipping_Calculator_Quote_OTP_Submit", {"PageName": "Shipping_Calculator", number: phoneValue});
      } else {
        setOtpIncorrect(true);
      }
    } catch (error) {
      setOtpIncorrect(true);
      console.error('Error verifying OTP:', error);
    } finally {
      setLoader(false);
    }
  };

  const verifyPhoneNumberCfForm = () => {
    const enteredOTP = Array.from({ length: 6 }, (_, index) =>
      document.getElementById(`digit${index + 1}`)?.value
    ).join('');
    if (enteredOTP.length === 6 && /^\d+$/.test(enteredOTP)) {
      verifyOTPCfForm(enteredOTP);
    } else {
      setOtpIncorrect(true);
    }
  };

  const moveToNextInputCfForm = (currentInput, nextInputId) => {
    const maxLength = parseInt(currentInput.getAttribute('maxLength'));
    if (currentInput.value.length === maxLength && nextInputId) {
      document.getElementById(nextInputId)?.focus();
    }
    // Only verify when the last digit is entered
    if (currentInput.id === 'digit6') {
      verifyPhoneNumberCfForm();
    }
  };

  const handleCountryChange = (e) => {
    console.log('Selected country:', e.target.value);
    const selectedCountry = GlobalBuyerData.country_Name.find(
      (option) => option.name === e.target.value
    );
    setCountryCode(selectedCountry ? `+${selectedCountry.code}` : '');
    setValue('country', e.target.value);

    // Clear error message when user selects country
    if (errorMessage) {
      setErrorMessage('');
    }

    // Reset phone verification status when country changes
    setPhVerifiedCfForm(false);
    setShowOtpField(false);
    setOtpIncorrect(false);
    setGetOtp(false);
    setIsResendMode(false);

    // Check phone validation based on newly selected country
    const phoneValue = watch('Phone');
    if (phoneValue && phoneValue.length === 10) {
      const regex = /^[0-9]*$/;

      if (e.target.value === 'India') {
        // For India, check if phone starts with valid digits and enable OTP
        if (regex.test(phoneValue) && !/^[0123]/.test(phoneValue)) {
          setOtpBtnDisabled(false);
        } else {
          setOtpBtnDisabled(true);
        }
      } else {
        // For non-Indian countries, auto-verify if phone is valid
        if (regex.test(phoneValue) && !/^[0123]/.test(phoneValue)) {
          setPhVerifiedCfForm(true);
          setOtpBtnDisabled(true);
        }
      }
    }
  };

  const handleFinalViewQuotes = async (data) => {
    const selectedCountryName = data.country;
    const isIndian = selectedCountryName === 'India';

    if (isIndian && !phVerifiedCfForm) {
      setErrorMessage('Please verify your phone number first.');
      return;
    }

    setFormSubmitted(true);
    setLoader(true);
    setErrorMessage('');

    try {
      const payload = {
        isShippingEst: true,
        name: data.Name,
        email: data.Email,
        companyName: data.CompanyName,
        financeNeeded: 0,
        phoneNumber: data.Phone,
        country: data.country,
        countryCode: countryCode || '+91', // Fallback to +91
        isExport: true,
        limitRequested: 0,
      };

      const response = await axios.post(ENDPOINTS.USER_CREATE, payload);
      if (response.status === 200) {
        setQuoteFlag(false);
        localStorage.setItem('quoteFlag', 'false');
        localStorage.setItem('userRegistered', 'true');
        setOpenQuotesPopup(false);
        setFormSubmitted(false);
        await fetchQuotesData();
        pushCleverTapEvent("Shipping_Calculator_Quote_Form_Submit", {"PageName": "Shipping_Calculator",  ...payload});
      }
    } catch (error) {
      console.error('Error submitting user details:', error);
      if (
        error.response?.data?.statusCode === 400 &&
        error.response?.data?.message === 'Credentials already exists'
      ) {
        setQuoteFlag(false);
        localStorage.setItem('quoteFlag', 'false');
        localStorage.setItem('userRegistered', 'true');
        setOpenQuotesPopup(false);
        setFormSubmitted(false);
        await fetchQuotesData();
      } else {
        setErrorMessage('Error submitting form. Please try again.');
        setFormSubmitted(false);
      }
    } finally {
      setLoader(false);
      setFormSubmitted(false);
    }
  };

  // Function to handle keypress and only allow numbers
  const handleKeyPress = (e) => {
    // Allow backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  // Function to clear error message when user types in any input field
  const handleInputChange = (fieldName, originalOnChange) => (e) => {
    if (errorMessage) {
      setErrorMessage('');
    }
    if (originalOnChange) {
      originalOnChange(e);
    }
    // Manually trigger the react-hook-form onChange
    setValue(fieldName, e.target.value, { shouldValidate: true });
  };

  return (
    <div className="fixed z-[1060] top-0 left-0 inset-0 overflow-y-auto w-full h-full">
      <div className="flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-[95%] p-3 w-[450px]">
          <div className="sm:w-full">
            <div className="bg-white sm:mt-2 lg:w-full sm:w-[100%]">
              <h2 className="text-center text-lg font-bold">Please fill your details here</h2>
              {errorMessage && (
                <p className="text-red-600 text-xs mt-2 text-center">{errorMessage}</p>
              )}
              <form onSubmit={handleSubmit(handleFinalViewQuotes)}>
                <div className="pt-5">
                  <input
                    {...register('Name')}
                    type="text"
                    placeholder="Full Name"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-1 placeholder-gray-400 text-sm font-normal"
                    onChange={handleInputChange('Name')}
                    onInput={handleNameInput}
                  />
                  <p className="text-red-600 text-xs mt-[3px]">{errors.Name?.message}</p>
                </div>
                <div className="pt-2">
                  <input
                    {...register('CompanyName')}
                    type="text"
                    placeholder="Company Name"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-1 placeholder-gray-400 text-sm font-normal"
                    onChange={handleInputChange('CompanyName')}
                  />
                  <p className="text-red-600 text-xs mt-[3px]">{errors.CompanyName?.message}</p>
                </div>
                <div className="pt-2">
                  <input
                    {...register('Email')}
                    type="email"
                    placeholder="Email"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-1 placeholder-gray-400 text-sm font-normal"
                    onChange={handleInputChange('Email')}
                  />
                  <p className="text-red-600 text-xs mt-[3px]">{errors.Email?.message}</p>
                </div>

                <div className="pt-2 relative">
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      placeholder="Code"
                      value={countryCode}
                      readOnly
                      className="block w-[75px] appearance-none border-0 border-gray-300 border-r px-1 h-[36px] placeholder-gray-400 text-sm font-normal text-gray-400 outline-0 absolute left-[5px]"
                    />
                    <select
                      {...register('country')}
                      onChange={handleCountryChange}
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 h-[42px] pl-[100px] placeholder-gray-400 text-sm font-normal cursor-pointer bg-white"
                    >
                      <option value="" disabled selected>
                        Select Country
                      </option>
                      {GlobalBuyerData?.country_Name.map((option) => (
                        <option key={option.id} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-red-600 text-xs mt-[3px]">{errors.country?.message}</p>
                </div>

                <div className="pt-2">
                  <div className="flex relative">
                    <input
                      {...register('Phone')}
                      onChange={handleInputChange('Phone', checkPhoneCfForm)}
                      onKeyDown={handleKeyPress}
                      onBlur={(e) => {
                        // Only re-validate phone when user clicks outside if OTP hasn't been sent yet
                        const currentCountry = watch('country');
                        if (currentCountry === 'India' && e.target.value && !showOtpField && !getOtp) {
                          checkPhoneCfForm(e);
                        }
                      }}
                      placeholder="Mobile number"
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-1 placeholder-gray-400 text-sm font-normal"
                      type="tel"
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    {phVerifiedCfForm && (
                      <div className="absolute flex items-center top-[5px] right-[90px] sm:right-[85px] z-10 bg-green-100 px-2 sm:px-1 text-xs text-green-600 h-[30px] rounded-md">
                        <span className="items-center flex">
                          <svg
                            className="mr-[5px]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="13"
                            height="13"
                          >
                            <path
                              d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11.0026 16L18.0737 8.92893L16.6595 7.51472L11.0026 13.1716L8.17421 10.3431L6.75999 11.7574L11.0026 16Z"
                              fill="rgba(41,158,34,1)"
                            />
                          </svg>
                          Verified
                        </span>
                      </div>
                    )}
                    {!phVerifiedCfForm && watch('country') === 'India' && (
                      <button
                        type="button"
                        disabled={otpBtnDisabled || !phoneValue}
                        onClick={sendOTPCfForm}
                        className="top-0 bottom-0 absolute right-0 rounded-r-md text-xs text-center px-1 w-[81px] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed bg-yellow-400 text-black"
                      >
                        {loader ? (
                          <ColorRing
                            visible={true}
                            height="30"
                            width="30"
                            ariaLabel="blocks-loading"
                            wrapperClass="ringForOtp"
                            colors={['#000']}
                          />
                        ) : otpBtnDisabled && countdown > 0 && isResendMode ? (
                          `${countdown}s`
                        ) : isResendMode ? (
                          'Resend OTP'
                        ) : (
                          'Send OTP'
                        )}
                      </button>
                    )}
                  </div>
                  <p className="text-red-600 text-xs mt-[3px]">{errors.Phone?.message}</p>
                </div>

                {showOtpField && !phVerifiedCfForm && watch('country') === 'India' && (
                  <>
                    <label className="text-sm font-medium text-gray-700 mb-0 mt-2 block">
                      Enter OTP
                    </label>
                    <div className="flex flex-wrap justify-between mt-[5px]">
                      {[...Array(6)].map((_, index) => (
                        <input
                          key={index}
                          className="h-[40px] px-1 text-xs text-center rounded-md border border-gray-400 min-w-0 w-[50px] sm:w-[40px]"
                          type="text"
                          id={`digit${index + 1}`}
                          onChange={(e) => {
                            if (e.target.value) {
                              moveToNextInputCfForm(e.target, `digit${index + 2}`);
                            }
                          }}
                          maxLength="1"
                          pattern="[0-9]"
                          required
                        />
                      ))}
                    </div>
                    {otpIncorrect && (
                      <div className="text-red-600 text-xs mt-[3px]">
                        Please enter correct OTP
                      </div>
                    )}
                  </>
                )}

                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={(watch('country') === 'India' && !phVerifiedCfForm) || formSubmitted}
                    className="py-1 px-3 rounded-md text-white text-lg font-normal w-full bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formSubmitted ? 'Processing...' : 'View Quotes'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setOpenQuotesPopup(false)}
                  className="absolute top-2 right-2"
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width={24}
                    height={24}
                  >
                    <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 10.5858L9.17157 7.75736L7.75736 9.17157L10.5858 12L7.75736 14.8284L9.17157 16.2426L12 13.4142L14.8284 16.2426L16.2426 14.8284L13.4142 12L16.2426 9.17157L14.8284 7.75736L12 10.5858Z" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: '' })}
        />
      )}
    </div>
  );
};

export default QUOTESFORMPOPUP;